import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { FEATURES, STATS } from "@/data/site";
import { Pill, ShieldAlert, Users, Bot } from "lucide-react";

const BENTO = [
  { ...FEATURES.find((f) => f.slug === "emergency-sos"), span: "md:col-span-7", sos: true },
  { ...FEATURES.find((f) => f.slug === "ai-family-coordinator"), span: "md:col-span-5", accent: true },
  { ...FEATURES.find((f) => f.slug === "medication-management"), span: "md:col-span-5" },
  { ...FEATURES.find((f) => f.slug === "emergency-circle"), span: "md:col-span-7" },
];

function cardStyle(feature) {
  if (feature.sos) return "border-rose-deep/30 bg-rose-deep text-sand";
  if (feature.accent) return "border-transparent bg-indigo text-sand";
  return "border-border bg-card";
}

function Card({ feature, index }) {
  const Icon = feature.icon;
  const filled = feature.sos || feature.accent;
  return (
    <Reveal
      delay={index * 0.06}
      className={`group relative flex flex-col justify-between overflow-hidden rounded-3xl border p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${feature.span} ${cardStyle(feature)}`}
    >
      <div
        className={`flex h-12 w-12 items-center justify-center rounded-2xl ${
          filled ? "bg-white/15 text-sand" : "bg-marigold/15 text-marigold-deep"
        }`}
      >
        <Icon className="h-6 w-6" strokeWidth={1.75} />
      </div>
      <div className="mt-10">
        <h3 className={`font-heading text-2xl font-semibold tracking-tight ${filled ? "text-sand" : "text-foreground"}`}>
          {feature.title}
        </h3>
        <p className={`mt-2 max-w-md text-base leading-relaxed ${filled ? "text-sand/85" : "text-muted-foreground"}`}>
          {feature.short}
        </p>
      </div>
      <Link
        to="/features"
        data-testid={`bento-link-${feature.slug}`}
        className={`mt-6 inline-flex w-fit items-center gap-1 text-sm font-semibold transition-colors ${
          filled ? "text-sand hover:text-sand/80" : "text-marigold-deep hover:text-rose-deep"
        }`}
      >
        Learn more
        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Link>
    </Reveal>
  );
}

export default function FeaturesBento() {
  return (
    <section id="features" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-20 sm:px-8 lg:py-28">
      <Reveal className="max-w-2xl">
        <p className="text-sm font-semibold uppercase tracking-[0.15em] text-marigold-deep">Everything in one place</p>
        <h2 className="mt-3 font-heading text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
          Powerful features, beautifully simple
        </h2>
        <p className="mt-4 text-lg text-muted-foreground">
          Kinect Nova AI brings together the tools families need to coordinate care
          and stay safe — without the complexity.
        </p>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-12">
        {BENTO.map((f, i) => (
          <Card key={f.slug} feature={f} index={i} />
        ))}
      </div>

      <div className="mt-14 grid grid-cols-2 gap-6 rounded-3xl border border-border bg-card p-8 md:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.05} className="text-center">
            <p className="font-mono-num text-3xl font-semibold text-foreground sm:text-4xl">{s.value}</p>
            <p className="mt-1 text-sm text-muted-foreground">{s.label}</p>
          </Reveal>
        ))}
      </div>

      <div className="mt-10 text-center">
        <Link
          to="/features"
          data-testid="see-all-features-btn"
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground transition-all hover:-translate-y-0.5 hover:bg-muted"
        >
          See all 11 features <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}
